import React, { useState, useEffect, useRef, useMemo } from 'react';
import { classNames } from 'primereact/utils';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { useFormik } from 'formik';
import { Toast } from 'primereact/toast';
import { translatedMessage } from '../../service/LanguageService';
import EnumService from '../../service/EnumService';
import { Calendar } from 'primereact/calendar';
import ValidationErrorModalComponent from '../../components/ValidationErrorModalComponent';
import { RecruitmentService } from '../../service/RecruitmentService';

const RecruitmentCampaignEdit = (props) => {
    let emptyRecruitmentCampaign = {
        id: null,
        startDate: '',
        endDate: '',
        status: 'INACTIVE',
        notification1Days: 0,
        notification2Days: 0
    };

    const [recruitmentCampaign, setRecruitmentCampaign] = useState(emptyRecruitmentCampaign);
    const [statusValues, setStatusValues] = useState([]);
    const [showValidationErrorDialog, setShowValidationErrorDialog] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [isNew, setIsNew] = useState(false);

    const recruitmentService = useMemo(() => new RecruitmentService(), []);

    const toast = useRef(null);

    useEffect(() => {
        const getEnumValues = async () => {
            const response = await EnumService.getEnumByName("RecruitmentCampaignStatus");
            setStatusValues(response)
        }

        getEnumValues();

        setRecruitmentCampaign(props?.value 
            ? { ...props.value, 
                notification1Days: props.value.notification1Days ? props.value.notification1Days : 0,
                notification2Days: props.value.notification2Days ? props.value.notification2Days : 0,
            } : emptyRecruitmentCampaign);
        setIsNew(props.value ? false : true);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props]);

    const hideRecruitmentCampaignDialog = () => {
        if (typeof props.visibleSetter === 'function') {
            props.visibleSetter(false);
        }
        setRecruitmentCampaign(emptyRecruitmentCampaign);
        formik.resetForm();
    };

    const saveRecruitmentCampaign = (data) => {
        recruitmentService.saveRecruitmentCampaign(data).then(
            (response) => {
                if (typeof props.afterSave === 'function') {
                    props.afterSave(response.recruitmentCampaign, isNew);
                }                
                hideRecruitmentCampaignDialog()
            },
            (error) => {
                // console.log(error)
                if (error?.hasValidationError) {
                    setValidationErrors(error.errorCodes)
                    setShowValidationErrorDialog(true)
                } else {
                    error && toast?.current?.show({ severity: 'error', summary: translatedMessage(error.errorMessage), life: 7000 })
                }
            }
        );
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: recruitmentCampaign ? recruitmentCampaign : emptyRecruitmentCampaign,
        validate: (data) => {
            let errors = {};

            if (!data.startDate) {
                errors.startDate = translatedMessage('form.error.recruitmentCampaign.startDate.required');
            } else if(data.id === null && data.startDate < new Date()) {        
                errors.startDate = translatedMessage('form.error.recruitmentCampaign.startDate.toEarly');       
            }

            if (!data.endDate) {
                errors.endDate = translatedMessage('form.error.educarecruitmentCampaigntion.endDate.required');
            }

            if (data.startDate > data.endDate) {
                errors.endDate = translatedMessage("form.error.recruitmentCampaign.endDate.toEarly");
            }

            if (!data.notification1Days) {
                errors.notification1Days = translatedMessage("form.error.recruitmentCampaign.notification1Days.required");
            }

            if (!data.notification2Days) {
                errors.notification2Days = translatedMessage("form.error.recruitmentCampaign.notification1Days.required");
            }

            if (data.notification1Days < data.notification2Days) {
                errors.notification2Days = translatedMessage("form.error.recruitmentCampaign.notification2Days.toEarly");
            }

            return errors;
        },
        onSubmit: (data) => {
            saveRecruitmentCampaign(data);
        }
    });

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);

    const getFormErrorMessage = (name) => {
        return isFormFieldValid(name) && <small className="p-error text-align-left">{formik.errors[name]}</small>;
    };

    const recruitmentCampaignDialogFooter = (
        <>
            <Button label={translatedMessage('generic.cancel')} icon="pi pi-times" className="p-button-text" onClick={hideRecruitmentCampaignDialog} />
            <Button label={translatedMessage('generic.save')} icon="pi pi-check" className="p-button p-button-primary" form="recruitmentCampaign-form" type="submit" />
        </>
    );

    return (
        <>
            <Toast ref={toast} />

            <Dialog
                visible={props?.visible}
                header={recruitmentCampaign?.id ? translatedMessage('recruitment.campaign.edit') : translatedMessage('recruitment.campaign.add')}
                modal
                resizable={false}
                className="pcn-dialog p-fluid"
                footer={recruitmentCampaignDialogFooter}
                onHide={hideRecruitmentCampaignDialog}
            >
                <form id="recruitmentCampaign-form" onSubmit={formik.handleSubmit}>
                    <div className="field p-inputgroup mt-3 mb-2">
                        <span className="p-float-label">
                            <Calendar id="startDate" name="startDate" value={formik.values.startDate} onChange={formik.handleChange} dateFormat="dd-mm-yy"
                                className={classNames({ 'p-invalid': isFormFieldValid('startDate') })} />
                            <label htmlFor="startDate" className={classNames({ 'p-error': isFormFieldValid('startDate') })}>
                                {translatedMessage("recruitment.campaign.startDate")} *
                            </label>
                        </span>
                    </div>
                    {getFormErrorMessage('startDate')}

                    <div className="field p-inputgroup mt-3 mb-2">
                        <span className="p-float-label">
                            <Calendar id="endDate" name="endDate" value={formik.values.endDate} onChange={formik.handleChange} dateFormat="dd-mm-yy"
                                className={classNames({ 'p-invalid': isFormFieldValid('endDate') })} />
                            <label htmlFor="endDate" className={classNames({ 'p-error': isFormFieldValid('endDate') })}>
                                {translatedMessage("recruitment.campaign.endDate")} *
                            </label>
                        </span>
                    </div>
                    {getFormErrorMessage('endDate')}

                    <div className="field p-inputgroup mt-3 mb-2">
                        <span className="p-float-label">
                            <InputText type="text" name="notification1Days" id="notification1Days"
                                value={formik.values.notification1Days} onChange={formik.handleChange}
                                className={classNames({ 'p-invalid': isFormFieldValid('notification1Days') })}
                                keyfilter="int" />
                            <label htmlFor="notification1Days" className={classNames({ 'p-error': isFormFieldValid('notification1Days') })}>
                                {translatedMessage("recruitment.campaign.notification1Days")} *
                            </label>
                        </span>
                    </div>
                    {getFormErrorMessage('notification1Days')}

                    <div className="field p-inputgroup mt-3 mb-2">
                        <span className="p-float-label">
                            <InputText type="text" name="notification2Days" id="notification2Days"
                                value={formik.values.notification2Days} onChange={formik.handleChange}
                                className={classNames({ 'p-invalid': isFormFieldValid('notification2Days') })}
                                keyfilter="int" />
                            <label htmlFor="notification2Days" className={classNames({ 'p-error': isFormFieldValid('notification2Days') })}>
                                {translatedMessage("recruitment.campaign.notification2Days")} *
                            </label>
                        </span>
                    </div>
                    {getFormErrorMessage('notification2Days')}

                    <div className="field p-inputgroup mt-3 mb-2">
                        <span className="p-float-label">
                            <Dropdown id="status" name="status" value={formik.values.status}
                                onChange={formik.handleChange} options={statusValues}>
                            </Dropdown>
                            <label htmlFor="status" className={classNames({ 'p-error': isFormFieldValid('status') })}>
                                {translatedMessage("generic.status")} *
                            </label>
                        </span>
                    </div>
                    {getFormErrorMessage('status')}

                </form>
            </Dialog>

            {showValidationErrorDialog &&
                <ValidationErrorModalComponent
                    visible={showValidationErrorDialog}
                    errors={validationErrors}
                    dialogTitle={translatedMessage("recruitment.campaign.validation.title")}
                    closeDialog={() => setShowValidationErrorDialog(false)}
                />
            }
        </>
    );
};

export default RecruitmentCampaignEdit;
